package twist.mena.sports.shared.dto.admin

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class AdminDeviceResponse(
    val token: String,
    @SerialName("twist_user_id") val twistUserId: String,
    val email: String? = null,
    val phone: String? = null,
    val platform: String,
    val locale: String,
    val timezone: String,
    val status: String,
    @SerialName("created_at_utc") val createdAtUtc: String,
    @SerialName("revoked_at_utc") val revokedAtUtc: String? = null
)

@Serializable
data class AdminTopicResponse(
    @SerialName("entity_type") val entityType: String,
    @SerialName("entity_id") val entityId: String,
    @SerialName("entity_name_ar") val entityNameAr: String? = null,
    @SerialName("entity_name_en") val entityNameEn: String? = null,
    @SerialName("subscriber_count") val subscriberCount: Int
)

@Serializable
data class AdminTopicSubscriberResponse(
    @SerialName("twist_user_id") val twistUserId: String,
    val events: List<String>,
    @SerialName("device_count") val deviceCount: Int
)

@Serializable
data class AdminSendNotificationRequest(
    @SerialName("title_ar") val titleAr: String,
    @SerialName("title_en") val titleEn: String,
    @SerialName("body_ar") val bodyAr: String? = null,
    @SerialName("body_en") val bodyEn: String? = null,
    @SerialName("target_type") val targetType: String,
    @SerialName("target_entity_type") val targetEntityType: String? = null,
    @SerialName("target_entity_id") val targetEntityId: String? = null,
    @SerialName("scheduled_at") val scheduledAt: String? = null,
    val link: String? = null,
    @SerialName("image_url") val imageUrl: String? = null,
    val platforms: List<String>? = null,
    val locales: List<String>? = null,
    @SerialName("min_age") val minAge: Int? = null,
    @SerialName("max_age") val maxAge: Int? = null
)

@Serializable
data class AdminAudienceEstimateResponse(
    @SerialName("estimated_count") val estimatedCount: Int
)

@Serializable
data class AdminNotificationHistoryResponse(
    val id: String,
    val type: String,
    @SerialName("title_ar") val titleAr: String,
    @SerialName("title_en") val titleEn: String,
    @SerialName("body_ar") val bodyAr: String? = null,
    @SerialName("body_en") val bodyEn: String? = null,
    @SerialName("entity_type") val entityType: String,
    @SerialName("entity_id") val entityId: String,
    @SerialName("delivery_status") val deliveryStatus: String,
    @SerialName("topic_name") val topicName: String? = null,
    @SerialName("admin_sender_id") val adminSenderId: String? = null,
    @SerialName("sent_at_utc") val sentAtUtc: String? = null,
    @SerialName("created_at_utc") val createdAtUtc: String,
    val link: String? = null,
    @SerialName("image_url") val imageUrl: String? = null,
    /** admin | vendor | system */
    val source: String = "admin",
    /** user | entities | broadcast | filtered */
    val audience: String = "user",
    @SerialName("event_code") val eventCode: String? = null,
    @SerialName("group_code") val groupCode: String? = null,
    @SerialName("skip_reason") val skipReason: String? = null,
    @SerialName("failed_reason") val failedReason: String? = null,
    @SerialName("vendor_event_id") val vendorEventId: String? = null,
    val entities: List<AdminNotificationEntityRef> = emptyList()
)

@Serializable
data class AdminNotificationAnalyticsResponse(
    @SerialName("total_sent") val totalSent: Int,
    @SerialName("total_delivered") val totalDelivered: Int,
    @SerialName("total_failed") val totalFailed: Int,
    @SerialName("total_pending") val totalPending: Int,
    @SerialName("sent_today") val sentToday: Int,
    @SerialName("sent_this_week") val sentThisWeek: Int,
    @SerialName("by_status") val byStatus: Map<String, Int>,
    @SerialName("by_type") val byType: Map<String, Int>
)

@Serializable
data class AdminUserSubscriptionResponse(
    @SerialName("entity_type") val entityType: String,
    @SerialName("entity_id") val entityId: String,
    @SerialName("entity_name_ar") val entityNameAr: String? = null,
    @SerialName("entity_name_en") val entityNameEn: String? = null,
    val events: List<String>
)

@Serializable
data class AdminSendNotificationResponse(
    val status: String,
    @SerialName("subscriber_count") val subscriberCount: Int? = null,
    val message: String? = null
)

@Serializable
data class AdminEntitySearchResult(
    @SerialName("entity_type") val entityType: String,
    @SerialName("entity_id") val entityId: String,
    @SerialName("name_en") val nameEn: String,
    @SerialName("name_ar") val nameAr: String? = null,
    @SerialName("image_url") val imageUrl: String? = null,
    @SerialName("legacy_id") val legacyId: String? = null
)

@Serializable
data class AdminScheduledNotificationResponse(
    val id: String,
    @SerialName("title_ar") val titleAr: String,
    @SerialName("title_en") val titleEn: String,
    @SerialName("body_ar") val bodyAr: String? = null,
    @SerialName("body_en") val bodyEn: String? = null,
    @SerialName("target_type") val targetType: String,
    @SerialName("target_value") val targetValue: String? = null,
    @SerialName("scheduled_at_utc") val scheduledAtUtc: String,
    val status: String,
    @SerialName("created_at_utc") val createdAtUtc: String,
    val link: String? = null,
    @SerialName("image_url") val imageUrl: String? = null
)

/** A team, league, player, match or article a notification relates to, by legacy id. */
@Serializable
data class AdminNotificationEntityRef(
    val type: String,
    val id: String
)
